

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    CircularProgress
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewLoyaltyLedgerModal from 'components/NewLoyaltyLedgerModal';

const LoyaltyLedgers = () => {
    const [ledgers, setLedgers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('loyalty_ledgers');

    useEffect(() => {
        fetchLedgers();
    }, []);

    const fetchLedgers = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/loyalty-ledgers/');
            setLedgers(response.data);
        } catch (error) {
            console.error('Error fetching loyalty ledgers:', error);
            setSnackbar({ open: true, message: 'Error loading loyalty ledgers', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveLedger = async (payload) => {
        try {
            await axiosInstance.post('/loyalty-ledgers/', payload);
            setSnackbar({ open: true, message: 'Loyalty Ledger created successfully', severity: 'success' });
            fetchLedgers();
        } catch (error) {
            console.error('Error saving loyalty ledger:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/loyalty-ledger/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Name",
            accessor: "name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        {
            Header: "Customer",
            accessor: "customer_id",
            width: "15%",
            Cell: ({ row }) => row.original.customer?.name || row.original.customer_id
        },
        { Header: "Type", accessor: "type", width: "12%" },
        { Header: "Source", accessor: "source", width: "12%" },
        { Header: "Points", accessor: "points", width: "10%" },
        { Header: "Status", accessor: "status", width: "10%" },
        {
            Header: "Monetary Value",
            accessor: "monetary_value",
            width: "12%",
            Cell: ({ value }) => value ? `$${parseFloat(value).toFixed(2)}` : '-'
        },
        {
            Header: "Created At",
            accessor: "created_at",
            width: "14%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: ledgers,
    }), [ledgers]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Loyalty Ledgers</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Ledger
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : ledgers.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Loyalty Ledger" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewLoyaltyLedgerModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveLedger}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default LoyaltyLedgers;

